import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from '../config/emailjs';
import './Registration.css';

const Registration: React.FC = () => {
  const { t, i18n } = useTranslation();
  
  // EmailJS configuration
  const { SERVICE_ID, CONFIRMATION_SERVICE_ID, TEMPLATE_ID, REGISTRATION_CONFIRMATION_TEMPLATE_ID, REGISTRATION_CONFIRMATION_TEMPLATE_ID_AL, PUBLIC_KEY, CONFIRMATION_PUBLIC_KEY } = EMAILJS_CONFIG;
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    organization: '',
    position: '',
    country: '',
    city: '',
    attendanceType: 'in-person',
    interests: [] as string[],
    howDidYouHear: '',
    additionalInfo: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      const checkboxValue = (e.target as HTMLInputElement).value;
      
      if (name === 'interests') {
        setFormData(prev => ({
          ...prev,
          interests: checked 
            ? [...prev.interests, checkboxValue]
            : prev.interests.filter(interest => interest !== checkboxValue)
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const fullName = `${formData.firstName} ${formData.lastName}`;
      
      // Prepare admin notification email template parameters
      const adminTemplateParams = {
        to_email: 'info@pejapeaceforum.org',
        to_name: 'Peja Peace Forum Team',
        from_name: fullName,
        from_email: formData.email,
        phone: formData.phone,
        organization: formData.organization,
        position: formData.position,
        country: formData.country,
        city: formData.city,
        attendance_type: formData.attendanceType === 'in-person' ? '🏛️ In-Person' : '💻 Virtual',
        interests: formData.interests.join(', '),
        interests_list: formData.interests.map(interest => `- ${interest}`).join('\n'),
        how_did_you_hear: formData.howDidYouHear,
        additional_info: formData.additionalInfo,
        message: `
New Registration for Peja Peace Forum 2025

Personal Information:
- Name: ${fullName}
- Email: ${formData.email}
- Phone: ${formData.phone}

Professional Information:
- Organization: ${formData.organization}
- Position: ${formData.position}

Location:
- Country: ${formData.country}
- City: ${formData.city}

Attendance:
- Type: ${formData.attendanceType === 'in-person' ? '🏛️ In-Person' : '💻 Virtual'}

Interests:
${formData.interests.map(interest => `- ${interest}`).join('\n')}

Additional Information:
- How did you hear about us: ${formData.howDidYouHear}
- Additional Info: ${formData.additionalInfo}

Registration submitted on: ${new Date().toLocaleString()}
        `
      };

      // Prepare user confirmation email template parameters
      const confirmationTemplateParams = {
        email: formData.email, // Your template uses {{email}} instead of {{to_email}}
        from_name: fullName,
        from_email: formData.email,
        organization: formData.organization,
        attendance_type: formData.attendanceType,
        city: formData.city,
        country: formData.country
      };

      // Send admin notification email first
      console.log('Sending admin notification...', {
        serviceId: SERVICE_ID,
        templateId: TEMPLATE_ID,
        publicKey: PUBLIC_KEY
      });
      
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        adminTemplateParams,
        PUBLIC_KEY
      );
      
      console.log('Admin notification sent successfully');
      
      // Send confirmation email (choose template based on language)
      const confirmationTemplateId = i18n.language === 'sq' ? REGISTRATION_CONFIRMATION_TEMPLATE_ID_AL : REGISTRATION_CONFIRMATION_TEMPLATE_ID;
      
      console.log('Sending confirmation email...', {
        serviceId: CONFIRMATION_SERVICE_ID,
        templateId: confirmationTemplateId,
        language: i18n.language,
        publicKey: CONFIRMATION_PUBLIC_KEY
      });
      
      await emailjs.send(
        CONFIRMATION_SERVICE_ID,
        confirmationTemplateId,
        confirmationTemplateParams,
        CONFIRMATION_PUBLIC_KEY
      );
      
      console.log('Confirmation email sent successfully');
      
      console.log('Registration and confirmation emails sent successfully');
      setSubmitStatus('success');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        organization: '',
        position: '',
        country: '',
        city: '',
        attendanceType: 'in-person',
        interests: [],
        howDidYouHear: '',
        additionalInfo: ''
      });
    } catch (error: any) {
      console.error('Registration error:', error);
      console.error('Error details:', {
        message: error?.message,
        text: error?.text,
        status: error?.status,
        serviceId: SERVICE_ID,
        confirmationServiceId: CONFIRMATION_SERVICE_ID,
        templateId: TEMPLATE_ID,
        confirmationTemplateId: REGISTRATION_CONFIRMATION_TEMPLATE_ID
      });
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const interestOptions = [
    t('registration.interests.peaceBuilding'),
    t('registration.interests.humanRights'),
    t('registration.interests.education'),
    t('registration.interests.youthDevelopment'),
    t('registration.interests.internationalRelations'),
    t('registration.interests.conflictResolution'),
    t('registration.interests.socialJustice'),
    t('registration.interests.communityDevelopment')
  ];

  return (
    <div className="registration">
      {/* Hero Section */}
      <section className="registration-hero section">
        <div className="container">
          <div className="registration-hero-content text-center">
            <h1>{t('registration.hero.title')}</h1>
            <p className="hero-subtitle">
              {t('registration.hero.subtitle')}
            </p>
            <div className="event-details">
              <div className="event-detail">
                <span className="detail-icon">📅</span>
                <span>{t('registration.hero.date')}</span>
              </div>
              <div className="event-detail">
                <span className="detail-icon">📍</span>
                <span>{t('registration.hero.location')}</span>
              </div>
              <div className="event-detail">
                <span className="detail-icon">🌐</span>
                <span>{t('registration.hero.format')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Form Section */}
      <section className="registration-form-section section">
        <div className="container">
          <div className="registration-content">
            <div className="registration-form-container">

              <div className="registration-form-header">
                <h2>{t('registration.form.title')}</h2>
                <p>{t('registration.form.description')}</p>
                
                <div className="whats-included">
                  <h3>{t('registration.benefits.title')}</h3>
                  <ul>
                    <li>{t('registration.benefits.networking')}</li>
                    <li>{t('registration.benefits.learning')}</li>
                    <li>{t('registration.benefits.certificate')}</li>
                    <li>{t('registration.benefits.materials')}</li>
                    <li>{t('registration.benefits.meals')}</li>
                  </ul>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="registration-form">

                {/* Personal Information */}
                <div className="form-section">
                  <h3>{t('registration.sections.personal')}</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">{t('registration.fields.firstName')} *</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="lastName">{t('registration.fields.lastName')} *</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="email">{t('registration.fields.email')} *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone">{t('registration.fields.phone')}</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>

                {/* Professional Information */}
                <div className="form-section">
                  <h3>{t('registration.sections.professional')}</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="organization">{t('registration.fields.organization')}</label>
                      <input
                        type="text"
                        id="organization"
                        name="organization"
                        value={formData.organization}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="position">{t('registration.fields.position')}</label>
                      <input
                        type="text"
                        id="position"
                        name="position"
                        value={formData.position}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>

                {/* Location Information */}
                <div className="form-section">
                  <h3>{t('registration.sections.location')}</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="country">{t('registration.fields.country')} *</label>
                      <input
                        type="text"
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="city">{t('registration.fields.city')} *</label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Attendance Information */}
                <div className="form-section">
                  <h3>{t('registration.sections.attendance')}</h3>
                  <div className="form-group">
                    <label>{t('registration.fields.attendanceType')} *</label>
                    <div className="radio-group">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="attendanceType"
                          value="in-person"
                          checked={formData.attendanceType === 'in-person'}
                          onChange={handleInputChange}
                        />
                        <span>{t('registration.options.inPerson')}</span>
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="attendanceType"
                          value="virtual"
                          checked={formData.attendanceType === 'virtual'}
                          onChange={handleInputChange}
                        />
                        <span>{t('registration.options.virtual')}</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Interests */}
                <div className="form-section">
                  <h3>{t('registration.sections.interests')}</h3>
                  <div className="checkbox-group">
                    {interestOptions.map((interest) => (
                      <label key={interest} className="checkbox-label">
                        <input
                          type="checkbox"
                          name="interests"
                          value={interest}
                          checked={formData.interests.includes(interest)}
                          onChange={handleInputChange}
                        />
                        <span>{interest}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Additional Information */}
                <div className="form-section">
                  <h3>{t('registration.sections.additional')}</h3>
                  <div className="form-group">
                    <label htmlFor="howDidYouHear">{t('registration.fields.howDidYouHear')}</label>
                    <select
                      id="howDidYouHear"
                      name="howDidYouHear"
                      value={formData.howDidYouHear}
                      onChange={handleInputChange}
                    >
                      <option value="">{t('registration.options.selectOption')}</option>
                      <option value="website">{t('registration.options.website')}</option>
                      <option value="social-media">{t('registration.options.socialMedia')}</option>
                      <option value="email">{t('registration.options.email')}</option>
                      <option value="colleague">{t('registration.options.colleague')}</option>
                      <option value="other">{t('registration.options.other')}</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="additionalInfo">{t('registration.fields.additionalInfo')}</label>
                    <textarea
                      id="additionalInfo"
                      name="additionalInfo"
                      value={formData.additionalInfo}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder={t('registration.placeholders.additionalInfo')}
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? t('registration.submitting') : t('registration.submit')}
                  </button>
                  
                  {submitStatus === 'success' && (
                    <div className="success-message" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '5px', color: '#155724' }}>
                      <h3 style={{ margin: '0 0 10px 0', fontSize: '18px' }}>✅ {t('registration.success.title')}</h3>
                      <p style={{ margin: '0', fontSize: '16px' }}>{t('registration.success.message')}</p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="error-message" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '5px', color: '#721c24' }}>
                      <h3 style={{ margin: '0 0 10px 0', fontSize: '18px' }}>❌ {t('registration.error.title')}</h3>
                      <p style={{ margin: '0', fontSize: '16px' }}>{t('registration.error.message')}</p>
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Registration;
